import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CorrespondingProductCardProps } from '@ds-mgnl/marketing';
import type { Option } from '@dsx/react';
import {
	getProductById,
	getProductRelationships,
} from '../services/productApi';
import { PRODUCT_IMAGE_FALLBACK } from '../services/productApi';
import type { ProductResponse, RelationshipGroup } from '../services/productApi';

export interface CorrespondingFilterDefinition {
	key: string;
	options: Option[];
}

export interface CorrespondingFilterOptions {
	filters: CorrespondingFilterDefinition[];
	hasQuickShip: boolean;
	sortOptions: Option[];
}

export interface CorrespondingFilters {
	selections: Record<string, string>;
	quickShipOnly: boolean;
}

interface ProductCardEntry {
	group: RelationshipGroup;
	card: CorrespondingProductCardProps;
}

function buildCard(
	group: RelationshipGroup,
	product: ProductResponse,
): CorrespondingProductCardProps {
	const specSheets =
		product.assets?.['spec-sheet'] ?? product.assets?.['Specification Sheet'];
	const revitFiles = product.assets?.['revit-file'];
	const imageSrc = product.images?.[0]?.contentUrl;

	return {
		image: {
			src: imageSrc ?? PRODUCT_IMAGE_FALLBACK,
			alt: group.groupName,
			objectFit: 'contain' as const,
		},
		productTitle: group.groupName,
		href: `/product/${group.productId}`,
		specSheetHref: specSheets?.[0]?.contentUrl,
		revitFileHref: revitFiles?.[0]?.contentUrl,
	};
}

const FILTER_KEY_TO_GROUP_FIELD: Record<string, keyof RelationshipGroup> = {
	material: 'materials',
	finish: 'finishes',
	shape: 'shapes',
	size: 'sizes',
};

function applyFilters(
	items: ProductCardEntry[],
	filters: CorrespondingFilters,
): ProductCardEntry[] {
	return items.filter(({ group }) => {
		if (filters.quickShipOnly && !group.isQuickShipAvailable) return false;
		for (const [key, value] of Object.entries(filters.selections)) {
			if (!value) continue;
			const field = FILTER_KEY_TO_GROUP_FIELD[key];
			if (!field) continue;
			const groupValues = group[field];
			if (Array.isArray(groupValues) && !groupValues.includes(value))
				return false;
		}
		return true;
	});
}

interface UseCorrespondingProductsResult {
	productCards: CorrespondingProductCardProps[];
	showsTableTops: boolean;
	isLoading: boolean;
	hasRelationships: boolean;
	totalCount: number;
	onPageChange: (page: number) => void;
	onFilterChange: (filters: CorrespondingFilters) => void;
	filterOptions: CorrespondingFilterOptions;
}

export function useCorrespondingProducts(
	product: ProductResponse | null,
): UseCorrespondingProductsResult {
	const [cardEntries, setCardEntries] = useState<ProductCardEntry[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [filters, setFilters] = useState<CorrespondingFilters>({
		selections: {},
		quickShipOnly: false,
	});

	const productType = product?.productType;
	const productIsBase = productType?.name === 'Dining Table Base';
	const productId = product?.id;
	const productTypeName = productType?.name;
	const shouldFetch = !!(productId && productType?.hasCorrespondingProducts);

	useEffect(() => {
		// * Reset for the new product on every id change (or when the current
		//   product can't have corresponding products). Doing this here rather
		//   than at render time keeps all of the hook's state transitions in one
		//   place and driven by a single dependency list.
		setCardEntries([]);
		setFilters({ selections: {}, quickShipOnly: false });

		if (!shouldFetch || !productId || !productTypeName) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		let mounted = true;

		async function load() {
			try {
				const response = await getProductRelationships(
					productId!,
					productTypeName!,
				);

				if (!mounted) return;

				const productResults = await Promise.allSettled(
					response.groups.map((g) => getProductById(g.productId)),
				);

				if (!mounted) return;

				const items: ProductCardEntry[] = [];
				for (let i = 0; i < response.groups.length; i++) {
					const result = productResults[i];
					if (result.status !== 'fulfilled') continue;
					const prod = result.value;
					if (!prod.id || prod.status !== 'active') continue;
					items.push({
						group: response.groups[i],
						card: buildCard(response.groups[i], prod),
					});
				}

				setCardEntries(items);
			} finally {
				if (mounted) setIsLoading(false);
			}
		}

		load();

		return () => {
			mounted = false;
		};
	}, [shouldFetch, productId, productTypeName]);

	const filteredItems = useMemo(
		() => applyFilters(cardEntries, filters),
		[cardEntries, filters],
	);

	const productCards = useMemo(
		() => filteredItems.map((item) => item.card),
		[filteredItems],
	);

	const onPageChange = useCallback(() => {}, []);

	const onFilterChange = useCallback((newFilters: CorrespondingFilters) => {
		setFilters(newFilters);
	}, []);

	const filterOptions = useMemo<CorrespondingFilterOptions>(() => {
		const materialSet = new Set<string>();
		const finishSet = new Set<string>();
		const shapeSet = new Set<string>();
		const sizeSet = new Set<string>();
		let hasQuickShip = false;

		for (const { group } of cardEntries) {
			for (const m of group.materials) materialSet.add(m);
			for (const f of group.finishes) finishSet.add(f);
			for (const s of group.shapes) shapeSet.add(s);
			for (const s of group.sizes) sizeSet.add(s);
			if (group.isQuickShipAvailable) hasQuickShip = true;
		}

		const buildOptions = (label: string, set: Set<string>) =>
			set.size > 0
				? [
						{ label, value: '' },
						...Array.from(set).sort().map((v) => ({ label: v, value: v })),
					]
				: [];

		const filters = [
			{ key: 'material', options: buildOptions('Material', materialSet) },
			{ key: 'finish', options: buildOptions('Finish', finishSet) },
			{ key: 'shape', options: buildOptions('Shape', shapeSet) },
			{ key: 'size', options: buildOptions('Size', sizeSet) },
		].filter((f) => f.options.length > 0);

		return { filters, hasQuickShip, sortOptions: [] };
	}, [cardEntries]);

	return {
		productCards,
		showsTableTops: productIsBase,
		isLoading,
		hasRelationships: cardEntries.length > 0,
		totalCount: productCards.length,
		onPageChange,
		onFilterChange,
		filterOptions,
	};
}
